import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { PHOTO_GUIDELINE_MESSAGE_IDS } from '../../util/photoGuidelines';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { H1, H2, LayoutSingleColumn, NamedLink, Page } from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { conditionItems, faqSections, sectionLinks, topFaqItems } from './HelpPage.content';

import css from './HelpPage.module.css';

const FaqItem = props => {
  const { item, defaultOpen = false } = props;

  return (
    <details id={item.id} className={css.faqItem} open={defaultOpen}>
      <summary className={css.faqQuestion}>
        <span>
          <FormattedMessage id={item.question} />
        </span>
        <span className={css.faqIcon} aria-hidden="true">
          +
        </span>
      </summary>
      <p className={css.faqAnswer}>
        <FormattedMessage id={item.answer} />
      </p>
    </details>
  );
};

const FaqSection = props => {
  const { section } = props;

  return (
    <section className={css.section} id={section.id}>
      <div className={css.sectionIntro}>
        <H2 rootClassName={css.sectionTitle}>
          <FormattedMessage id={section.title} />
        </H2>
        {section.intro ? (
          <p className={css.sectionText}>
            <FormattedMessage id={section.intro} />
          </p>
        ) : null}
      </div>
      <div className={css.faqList}>
        {section.items.map((item, index) => (
          <FaqItem
            key={item.question}
            item={item}
            defaultOpen={item.open || (section.openFirst && index === 0)}
          />
        ))}
      </div>
      {section.link ? (
        <NamedLink className={css.sectionLink} name={section.link.routeName}>
          <FormattedMessage id={section.link.label} />
        </NamedLink>
      ) : null}
    </section>
  );
};

const TopQuestions = () => (
  <section className={css.topQuestions}>
    <div className={css.sectionIntro}>
      <p className={css.sectionEyebrow}>
        <FormattedMessage id="HelpPage.topFaq.eyebrow" />
      </p>
      <H2 rootClassName={css.sectionTitle}>
        <FormattedMessage id="HelpPage.topFaq.title" />
      </H2>
    </div>
    <div className={css.faqList}>
      {topFaqItems.map((item, index) => (
        <FaqItem key={item.question} item={item} defaultOpen={index === 0} />
      ))}
    </div>
  </section>
);

const ContactCta = () => (
  <section className={css.contactCta}>
    <div>
      <h2 className={css.contactCtaTitle}>
        <FormattedMessage id="HelpPage.contactCta.title" />
      </h2>
      <p className={css.contactCtaText}>
        <FormattedMessage id="HelpPage.contactCta.text" />
      </p>
    </div>
    <NamedLink className={css.primaryButton} name="ContactPage">
      <FormattedMessage id="HelpPage.contactCta.button" />
    </NamedLink>
  </section>
);

const TopicSidebar = props => {
  const { intl } = props;

  return (
    <aside className={css.sideNav}>
      <p className={css.sideNavTitle}>
        <FormattedMessage id="HelpPage.sideNav.title" />
      </p>
      <nav aria-label={intl.formatMessage({ id: 'HelpPage.nav.ariaLabel' })}>
        {sectionLinks.map(link => (
          <a key={link.href} className={css.sideNavLink} href={link.href}>
            <FormattedMessage id={link.label} />
          </a>
        ))}
      </nav>
    </aside>
  );
};

const BuyerProtectionSummary = () => (
  <section className={css.section} id="kaeuferschutz">
    <div className={css.sectionIntro}>
      <H2 rootClassName={css.sectionTitle}>
        <FormattedMessage id="HelpPage.buyerProtection.title" />
      </H2>
    </div>
    <div className={css.summaryPanel}>
      <p className={css.summaryText}>
        <FormattedMessage id="HelpPage.buyerProtection.summary" />
      </p>
      <p className={css.summaryText}>
        <FormattedMessage id="HelpPage.buyerProtection.problemAction" />
      </p>
      <p className={css.summaryText}>
        <FormattedMessage id="HelpPage.buyerProtection.proofNotice" />
      </p>
      <p className={css.summaryDisclaimer}>
        <FormattedMessage id="HelpPage.buyerProtection.disclaimer" />
      </p>
      <NamedLink className={css.sectionLink} name="BuyerProtectionPage">
        <FormattedMessage id="HelpPage.buyerProtection.moreLink" />
      </NamedLink>
    </div>
  </section>
);

export const ConditionsSection = () => (
  <section className={css.section} id="zustaende">
    <div className={css.sectionIntro}>
      <H2 rootClassName={css.sectionTitle}>
        <FormattedMessage id="HelpPage.conditions.title" />
      </H2>
      <p className={css.sectionText}>
        <FormattedMessage id="HelpPage.conditions.intro" />
      </p>
    </div>
    <div className={css.conditionGrid}>
      {conditionItems.map(item => (
        <article key={item.title} className={css.conditionCard}>
          <h3 className={css.conditionTitle}>
            <FormattedMessage id={item.title} />
          </h3>
          <p className={css.conditionText}>
            <FormattedMessage id={item.text} />
          </p>
        </article>
      ))}
    </div>
    <div className={css.photoGuide}>
      <h3 className={css.photoGuideTitle}>
        <FormattedMessage id="PhotoGuidelines.title" />
      </h3>
      <p className={css.photoGuideIntro}>
        <FormattedMessage id="PhotoGuidelines.intro" />
      </p>
      <ul className={css.photoChecklist}>
        {PHOTO_GUIDELINE_MESSAGE_IDS.map(messageId => (
          <li key={messageId} className={css.photoChecklistItem}>
            <FormattedMessage id={messageId} />
          </li>
        ))}
      </ul>
      <NamedLink className={css.sectionLink} name="CMSPage" params={{ pageId: 'zustand' }}>
        <FormattedMessage id="PhotoGuidelines.guideLink" />
      </NamedLink>
    </div>
    <div className={css.notice}>
      <FormattedMessage id="HelpPage.conditions.damageNotice" />
    </div>
  </section>
);

const ContactSection = () => (
  <section className={css.contactSection} id="kontakt">
    <div className={css.contactBox}>
      <p className={css.sectionEyebrow}>
        <FormattedMessage id="HelpPage.contact.eyebrow" />
      </p>
      <H2 rootClassName={css.sectionTitle}>
        <FormattedMessage id="HelpPage.contact.title" />
      </H2>
      <p className={css.sectionText}>
        <FormattedMessage
          id="HelpPage.contact.text"
          values={{
            contactLink: (
              <NamedLink className={css.inlineLink} name="ContactPage">
                <FormattedMessage id="HelpPage.contact.formLink" />
              </NamedLink>
            ),
          }}
        />
      </p>
      <p className={css.contactNote}>
        <FormattedMessage id="HelpPage.contact.note" />
      </p>
      <p className={css.contactNote}>
        <FormattedMessage
          id="HelpPage.contact.emailText"
          values={{
            emailLink: (
              <a className={css.inlineLink} href="mailto:info@racketport.com">
                info@racketport.com
              </a>
            ),
          }}
        />
      </p>
    </div>
  </section>
);

/**
 * Public help and FAQ page for RacketPort buyers and sellers.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
export const HelpPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;
  const title = intl.formatMessage({ id: 'HelpPage.schemaTitle' }, { marketplaceName });
  const description = intl.formatMessage({ id: 'HelpPage.schemaDescription' }, { marketplaceName });

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        mainColumnClassName={css.layoutMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <section className={css.hero}>
            <div className={css.heroInner}>
              <p className={css.eyebrow}>
                <FormattedMessage id="HelpPage.hero.eyebrow" />
              </p>
              <H1 rootClassName={css.heroTitle}>
                <FormattedMessage id="HelpPage.hero.title" />
              </H1>
              <p className={css.heroText}>
                <FormattedMessage id="HelpPage.hero.text" />
              </p>
              <nav
                className={css.quickNav}
                aria-label={intl.formatMessage({ id: 'HelpPage.nav.ariaLabel' })}
              >
                {sectionLinks.map(link => (
                  <a key={link.href} className={css.quickLink} href={link.href}>
                    <FormattedMessage id={link.label} />
                  </a>
                ))}
              </nav>
            </div>
          </section>

          <div className={css.contentShell}>
            <TopicSidebar intl={intl} />
            <main className={css.contentMain}>
              <TopQuestions />
              <ContactCta />
              {faqSections.slice(0, 4).map(section => (
                <FaqSection key={section.id} section={section} />
              ))}
              <BuyerProtectionSummary />
              <ConditionsSection />
              {faqSections.slice(4).map(section => (
                <FaqSection key={section.id} section={section} />
              ))}
              <ContactSection />
            </main>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

HelpPageComponent.propTypes = {
  scrollingDisabled: bool,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const HelpPage = compose(connect(mapStateToProps))(HelpPageComponent);

export default HelpPage;
